import { useTranslation } from 'next-i18next'
import React, { Fragment, useRef, useState } from 'react'

import Duration from '../Duration/Duration'
import FilterButton from '../FilterButton/FilterButton'
import KeyFilter from '../KeyFilter/KeyFilter'
import TempoBpm from '../TempoBpm/TempoBpm'
import VocalAndLyricsFilter from '../VocalAndLyricsFilter/VocalAndLyricsFilter'
import ProFilter from './../ProFilter/ProFilter'
import styles from './AdvancedFilterTabs.module.css'

const AdvancedFilterTabs = (): JSX.Element => {
	const { t } = useTranslation(['search'])
	const [activeTab, setActiveTab] = useState(0)
	const tabsRef = useRef()

	const toggleTab = (tab: number): void => {
		setActiveTab(tab)
	}

	const advancedFilters = [
		{
			title: t('duration'),
			component: (
				<Duration data-testid="duration-filter" hidden={activeTab !== 0} />
			),
		},
		{
			title: t('tempo-bpm'),
			component: (
				<TempoBpm data-testid="tempo-bpm-filter" hidden={activeTab !== 1} />
			),
		},
		{
			title: t('pro'),
			component: (
				<ProFilter data-testid="pro-filter" hidden={activeTab !== 2} />
			),
		},
		{
			title: t('key-metre'),
			component: (
				<KeyFilter data-testid="key-metre-filter" hidden={activeTab !== 3} />
			),
		},
		{
			title: t('lyrics'),
			component: (
				<VocalAndLyricsFilter
					data-testid="vocal-and-lyrics-filter"
					hidden={activeTab !== 4}
				/>
			),
		},
	]

	const handleKeydown = (
		event: React.KeyboardEvent<HTMLButtonElement>
	): void => {
		if (!tabsRef.current) {
			return
		}

		const { key } = event

		let nextActiveTab: number

		if (key === 'ArrowLeft') {
			nextActiveTab =
				activeTab === 0 ? advancedFilters.length - 1 : activeTab - 1
		}

		if (key === 'ArrowRight') {
			nextActiveTab =
				activeTab === advancedFilters.length - 1 ? 0 : activeTab + 1
		}

		if (nextActiveTab >= 0) {
			toggleTab(nextActiveTab)
		}

		const newTabElement = (tabsRef.current as HTMLElement).querySelectorAll(
			'[role="tab"]'
		)[nextActiveTab] as HTMLElement

		newTabElement.focus()
	}

	return (
		<>
			<div className={styles.tabs} ref={tabsRef} role="tablist">
				{advancedFilters.map((filter, index) => (
					<FilterButton
						aria-controls={`${filter.title.replace(/\s/g, '')}-panel`}
						aria-selected={activeTab === index}
						key={`${filter.title.replace(/\s/g, '')}-button`}
						onClick={() => {
							toggleTab(index)
						}}
						onKeyDown={handleKeydown}
						role="tab"
						tabIndex={activeTab === index ? 0 : -1}
						title={filter.title}
					/>
				))}
			</div>
			<div className={styles.panelWrapper}>
				{advancedFilters.map((filter) => (
					<Fragment key={`${filter.title.replace(/\s/g, '')}-panel`}>
						{filter.component}
					</Fragment>
				))}
			</div>
		</>
	)
}

export default AdvancedFilterTabs
